import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faDownload,
  faEye,
  faEyeSlash,
  faGlobe,
  faMobile,
  faPrint,
  faShare,
  faShareAll,
  faTasks,
  faTimesCircle,
  faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import { Button, Tools } from '@domain-group/suit-theme'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import { useSetState } from '@domain-group/react-hooks'

import handleCallbackAction from '@functions/handleCallbackAction'
import { confirmDelete } from '@functions/entity'

import useCampaign from '@hooks/useCampaign'
import useFlipperKey from '@hooks/useFlipperKey'

import type { UseCampaignImagesPayload } from '../../hooks/useCampaignImages'

interface ToolsProps {
  campaignImagesPayload: UseCampaignImagesPayload,
}

const defaultState = {
  updatingPrivate: false,
  updatingPublic: false,
}

const PhotosAndPlansTools = (props: ToolsProps) => {
  const [state, setState] = useSetState(defaultState)
  const { updatingPrivate, updatingPublic } = state

  const { campaignImagesPayload } = props

  const {
    allSelected,
    bulkSelecting,
    callbacks: {
      apiExportImages: exportFn,
      buildProofSheetLink,
      bulkDeleteImages: bulkDeleteFn,
      bulkUpdateImages: bulkUpdateFn,
      copyLink,
      deselectAll,
      downloadMedia,
      resetExportSizeErrors,
      saveBulkSelectSort,
      selectSpecifiedPortalImageIds,
      setState: setCampaignImagesState,
      selectAll,
    },
    campaign,
    canExportImages,
    deleting,
    hasFilteredImages,
    hasSelectedImages,
    hasSpecifiedPortalImages,
    isGridView,
  } = campaignImagesPayload
  const { isEnabled: photosPlansV2 } = useFlipperKey('photos_plans_v2')
  const campaignPayload = useCampaign(campaign)
  const { callbacks: { selectSMSRecipient } } = campaignPayload

  const updateImageToPrivatePayload = {
    callbacks: {
      action: bulkUpdateFn,
      afterAction: () => setState({ updatingPrivate: false }),
    },
    entityParams: { private: true },
    toastText: 'Images Updated',
  }

  const updateImageToPublicPayload = {
    callbacks: {
      action: bulkUpdateFn,
      afterAction: () => {
        setState({ updatingPublic: false })
        resetExportSizeErrors()
      },
    },
    entityParams: { private: false },
    toastText: 'Images Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: deselectAll,
      deleteFn: bulkDeleteFn,
    },
    options: {
      isBulkDelete: true,
    },
    strings: {
      confirmString: 'Are you sure? They cannot be recovered.',
      entityName: 'Selected Images',
    },
  }

  const apiExportImagesPayload = {
    callbacks: {
      action: exportFn,
    },
    toastText: 'Image Export Queued',
  }

  return (
    <Tools>
      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={faMobile} />}
        onClick={() => selectSMSRecipient({
          activityKey: 'media.shared',
          endPoint: `campaigns/${campaign.id}/users`,
          linkAnchor: 'media',
        })}
        size="medium"
      >
        Send Dashboard Link
      </Button>

      {isGridView && (
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={bulkSelecting ? faCheckCircle : faTasks} />}
          marginTop="medium"
          onClick={bulkSelecting
            ? () => saveBulkSelectSort()
            : () => setCampaignImagesState({ bulkSelecting: !bulkSelecting })}
          size="medium"
        >
            {bulkSelecting ? 'Save Image Sort' : 'Bulk Select Images'}
        </Button>
      )}
      {hasFilteredImages && photosPlansV2 && (
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faCheckCircle} />}
          marginTop="medium"
          onClick={selectAll}
          size="medium"
        >
          Select All
        </Button>
      )}
      {hasSpecifiedPortalImages && (
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faGlobe} />}
          marginTop="medium"
          onClick={() => selectSpecifiedPortalImageIds()}
          size="medium"
        >
          Select Specified Portal Images
        </Button>
      )}

      {hasSelectedImages && (
        <>
          {!photosPlansV2 && <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
            marginTop="medium"
            onClick={selectAll}
            size="medium"
          >
            Select All
          </Button>}
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTimesCircle} />}
            marginTop="medium"
            onClick={deselectAll}
            size="medium"
          >
            Deselect All
          </Button>

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faShare} />}
            marginTop="medium"
            onClick={() => copyLink()}
            size="medium"
          >
            Share
          </Button>

          {canExportImages && (
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faShareAll} />}
              marginTop="medium"
              onClick={() => handleCallbackAction(apiExportImagesPayload)}
              size="medium"
            >
              Export
            </Button>
          )}

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faDownload} />}
            marginTop="medium"
            onClick={() => downloadMedia()}
            size="medium"
          >
            Download
          </Button>

          <AuthenticatedDownload>
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="secondaryUtility"
              href={buildProofSheetLink()}
              icon={<FontAwesomeIcon icon={faPrint} />}
              marginTop="medium"
              size="medium"
            >
              Proof Sheet
            </Button>
          </AuthenticatedDownload>

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            loading={deleting}
            marginTop="medium"
            // @ts-expect-error TS2345
            onClick={() => confirmDelete(confirmDeletePayload)}
            size="medium"
          >
            Delete
          </Button>

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faEye} />}
            loading={updatingPublic}
            marginTop="medium"
            onClick={() => {
              setState({ updatingPublic: true })
              // @ts-expect-error TS2345
              handleCallbackAction(updateImageToPublicPayload)
            }}
            size="medium"
          >
            Make Public
          </Button>

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faEyeSlash} />}
            loading={updatingPrivate}
            marginTop="medium"
            onClick={() => {
              setState({ updatingPrivate: true })
              // @ts-expect-error TS2345
              handleCallbackAction(updateImageToPrivatePayload)
            }}
            size="medium"
          >
            Make Private
          </Button>
        </>
      )}
    </Tools>
  )
}

export default PhotosAndPlansTools
